import { array, number, object, string } from "yup";

import { ICampaign, ICampaignDiscount, ISegment } from "@/interfaces/campaign.interface";
import IContact from "@/interfaces/contact.interface";
import { IOrderInput, IOrderItem } from "@/interfaces/order.interfaces";
import { ISku } from "@/interfaces/sku.interface";
import { ItemsTableValidationMessages } from "@/messages/orders/order-items-validation.messages";
import campaignService from "@/services/campaign.service";
import contactService from "@/services/contact.service";
import { CampaignStatus, CampaignType } from "@/types/campaign.types";

export const buildOrderItemsFormSchema = (messages: ItemsTableValidationMessages) => {
  return object().shape({
    items: array().of(
      object().shape({
        quantity: number()
          .required(messages.quantityRequired)
          .moreThan(0, messages.quantityMoreThanZero)
          .notOneOf([0], messages.quantityMoreThanZero),
        netUnitPrice: number()
          .required(messages.netUnitPriceRequired)
          .moreThan(0, messages.netUnitPriceMoreThanZero)
          .notOneOf([0], messages.netUnitPriceMoreThanZero),
        netLineAmount: number()
          .required(messages.netLineAmountRequired)
          .moreThan(0, messages.netLineAmountMoreThanZero)
          .notOneOf([0], messages.netLineAmountMoreThanZero),
        userComment: string().when('needComment', {
          is: true,
          then: (schema) => schema.required(messages.userCommentRequired),
          otherwise: (schema) => schema.nullable(),
        }),
      })
    ),
  });
};

const isPromotionValid = (
  startPromoDate: string | null | undefined,
  endPromoDate: string | null | undefined
): boolean => {
  if (!startPromoDate || !endPromoDate) return false;

  const currentDate = new Date();
  const startDate = new Date(startPromoDate);
  const endDate = new Date(endPromoDate);

  return currentDate >= startDate && currentDate <= endDate;
};

const calculateNetLineAmount = (quantity: number, netUnitPrice: number): number => {
  return quantity * netUnitPrice;
};

const calculateGrossLineAmount = (quantity: number, grossUnitPrice: number): number => {
  return quantity * grossUnitPrice;
};

const generateRandomNumber = (): number => {
  const min = 100000000000000;
  const max = 999999999999999;
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

export const getSkuListPrice = (sku: ISku): number => {
  const isPromoValid = sku.promoPrice && isPromotionValid(sku?.startPromoPrice, sku.endPromoPrice);
  return Number(isPromoValid ? sku.promoPrice : sku.mainPrice) || 0;
};

type TransformSkuOptions = {
  quantity?: number;
  discountCouponCode?: string;
  campaignId?: number | null;
  campaignNetUnitPrice?: number;
};

const roundMoney = (value: number): number => Number(value.toFixed(2));

export const getOrderItemTotals = (items: IOrderItem[] | null | undefined) => {
  const productItems = (items || []).filter((item) => item.lineType !== 2);
  const netTotal = roundMoney(
    productItems.reduce((acc, item) => acc + Number(item.netLineAmount || 0), 0)
  );
  const grossTotal = roundMoney(
    productItems.reduce((acc, item) => {
      const gross = Number(item.grossLineAmount);
      return acc + (gross > 0 ? gross : Number(item.netLineAmount || 0));
    }, 0)
  );

  return {
    netTotal,
    grossTotal,
    couponDiscount: roundMoney(Math.max(0, grossTotal - netTotal)),
  };
};

export const getExpectedOrderAmount = (values: {
  items?: IOrderItem[] | null;
  orderFreight?: number;
  orderCharges?: number;
  orderDiscount?: number;
  giftPackingPrice?: number;
  discountCouponAmount?: number;
  orderAmount?: number;
}) => {
  const { netTotal, couponDiscount } = getOrderItemTotals(values.items);
  const campaignDiscount =
    Number(values.discountCouponAmount) > 0 ? Number(values.discountCouponAmount) : couponDiscount;
  const extraDiscount = campaignDiscount > 0 ? 0 : Number(values.orderDiscount || 0);

  return roundMoney(
    netTotal +
      Number(values.orderFreight || 0) +
      Number(values.orderCharges || 0) -
      extraDiscount +
      Number(values.giftPackingPrice || 0)
  );
};

export const applyPercentageDiscount = (basePrice: number, percentage: number): number => {
  if (!basePrice || !percentage || percentage <= 0) return basePrice;
  const discount = roundMoney((basePrice * percentage) / 100);
  return roundMoney(basePrice - discount);
};

const normalizeCampaignDiscount = (
  discount: ICampaignDiscount | Record<string, unknown> | null
): ICampaignDiscount | null => {
  if (!discount) return null;

  const raw = discount as Record<string, unknown>;
  return {
    campaignType: Number(raw.campaignType ?? raw.CampaignType ?? 0),
    discountedPrice: Number(raw.discountedPrice ?? raw.DiscountedPrice ?? 0),
    promoPrice: Number(raw.promoPrice ?? raw.PromoPrice ?? 0),
    minimumQuantity: Number(raw.minimumQuantity ?? raw.MinimumQuantity ?? 1),
  };
};

export const applyCampaignDiscountToPrice = (
  basePrice: number,
  quantity: number,
  discount: ICampaignDiscount | null
): number => {
  const normalized = normalizeCampaignDiscount(discount);
  if (!normalized) return basePrice;

  const minQuantity =
    normalized.minimumQuantity && normalized.minimumQuantity > 0 ? normalized.minimumQuantity : 1;
  if (quantity < minQuantity) return basePrice;
  if (normalized.promoPrice > 0) return normalized.promoPrice;
  if (normalized.discountedPrice > 0) return normalized.discountedPrice;

  return basePrice;
};

const parseSegmentList = (value: string | string[] | null | undefined): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }

  const trimmed = value.trim();
  if (!trimmed || trimmed === "[]" || trimmed === "null") return [];

  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      return parsed
        .map((item) => String(item).replace(/^\[|\]$/g, "").replace(/^"|"$/g, "").trim())
        .filter(Boolean);
    }
  } catch {
    // comma-separated fallback below
  }

  return trimmed
    .split(",")
    .map((item) => item.replace(/^\[|\]$/g, "").replace(/^"|"$/g, "").trim())
    .filter(Boolean);
};

const getContactAge = (birthDate: string | Date | null | undefined): number | null => {
  if (!birthDate) return null;
  const birth = new Date(birthDate);
  if (Number.isNaN(birth.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }
  return age;
};

const hasSegmentConstraints = (segment?: ISegment | null): boolean => {
  if (!segment) return false;
  return Boolean(
    segment.birthdayPeriod ||
      segment.gender ||
      segment.contactSource ||
      segment.minimumAge ||
      segment.maximumAge ||
      parseSegmentList(segment.cities).length > 0 ||
      parseSegmentList(segment.contactTag).length > 0
  );
};

export const isCampaignCurrentlyValid = (campaign: ICampaign | null | undefined): boolean => {
  if (!campaign) return false;
  if (String(campaign.status).toUpperCase() !== CampaignStatus.Active) return false;
  if (!campaign.startDate || !campaign.endDate) return false;

  const now = new Date();
  const startDate = new Date(campaign.startDate);
  const endDate = new Date(campaign.endDate);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return false;

  if (endDate.getHours() === 0 && endDate.getMinutes() === 0 && endDate.getSeconds() === 0) {
    endDate.setHours(23, 59, 59, 999);
  }

  return now >= startDate && now <= endDate;
};

export const contactMatchesCampaignSegment = (
  campaign: ICampaign | null | undefined,
  contact: IContact | null | undefined
): boolean => {
  if (!campaign) return false;
  if (!hasSegmentConstraints(campaign.segment)) return true;
  if (!contact || contact.active === false) return false;

  const segment = campaign.segment;

  if (segment.gender && String(contact.gender) !== String(segment.gender)) return false;

  if (segment.birthdayPeriod) {
    if (!contact.birthDate) return false;
    const birth = new Date(contact.birthDate);
    if (Number.isNaN(birth.getTime())) return false;
    const birthMonth = String(birth.getMonth() + 1).padStart(2, "0");
    const months = segment.birthdayPeriod.split(",").map((month) => month.trim().padStart(2, "0"));
    if (!months.includes(birthMonth)) return false;
  }

  const minAge = segment.minimumAge === "" || segment.minimumAge == null ? null : Number(segment.minimumAge);
  const maxAge = segment.maximumAge === "" || segment.maximumAge == null ? null : Number(segment.maximumAge);
  if (minAge != null || maxAge != null) {
    const age = getContactAge(contact.birthDate);
    if (age == null) return false;
    if (minAge != null && !Number.isNaN(minAge) && age < minAge) return false;
    if (maxAge != null && !Number.isNaN(maxAge) && age > maxAge) return false;
  }

  const cities = parseSegmentList(segment.cities);
  if (cities.length > 0) {
    const contactCity = (contact.addressCity || "").trim().toLowerCase();
    if (!contactCity || !cities.some((city) => city.toLowerCase() === contactCity)) return false;
  }

  const tags = parseSegmentList(segment.contactTag);
  if (tags.length > 0) {
    const contactTags = Array.isArray(contact.tags) ? contact.tags.join(",") : String(contact.tags || "");
    const hasTag = tags.some((tag) => contactTags.includes(`"[${tag}]"`) || contactTags.includes(tag));
    if (!hasTag) return false;
  }

  return true;
};

export const isCampaignEligible = (
  campaign: ICampaign | null | undefined,
  contact: IContact | null | undefined
): boolean => isCampaignCurrentlyValid(campaign) && contactMatchesCampaignSegment(campaign, contact);

export const applyCampaignRulesToPrice = (
  campaign: ICampaign | null,
  productId: number | null | undefined,
  basePrice: number,
  quantity: number
): number | undefined => {
  if (!campaign || !basePrice) return undefined;

  const campaignType = Number(campaign.type);
  if (campaignType === CampaignType.SimpleMessage) return undefined;

  const items = campaign.items || [];
  const hasItems = items.length > 0;
  const campaignItem = hasItems
    ? items.find((item) => productId && Number(item.productId) === Number(productId))
    : undefined;

  if (campaignType === CampaignType.DiscountCoupon) {
    if (hasItems) {
      if (!campaignItem) return undefined;
      const minQuantity = campaignItem.minimumQuantity > 0 ? Number(campaignItem.minimumQuantity) : 1;
      if (quantity < minQuantity) return undefined;
      const promoPrice = Number(campaignItem.promoPrice);
      return promoPrice > 0 ? roundMoney(promoPrice) : undefined;
    }

    const percentage = Number(campaign.discountPercentage ?? 0);
    if (percentage <= 0) return undefined;
    const discountedPrice = applyPercentageDiscount(basePrice, percentage);
    return discountedPrice > 0 && discountedPrice !== basePrice ? discountedPrice : undefined;
  }

  if (campaignType === CampaignType.PromotionalPrice) {
    if (!campaignItem) return undefined;
    const minQuantity = campaignItem.minimumQuantity > 0 ? Number(campaignItem.minimumQuantity) : 1;
    if (quantity < minQuantity) return undefined;
    const promoPrice = Number(campaignItem.promoPrice);
    return promoPrice > 0 ? roundMoney(promoPrice) : undefined;
  }

  return undefined;
};

export const resolveCampaign = async (
  campaignId: number | null | undefined,
  couponCode?: string | null
): Promise<ICampaign | null> => {
  if (campaignId) {
    try {
      const campaignById = await campaignService.getById(campaignId);
      if (isCampaignCurrentlyValid(campaignById)) return campaignById;
    } catch {
      // fallback by coupon below
    }
  }

  const code = couponCode?.trim();
  if (!code) return null;

  const campaignSummary = await campaignService.findByCode(code);
  if (!campaignSummary?.id) return null;

  try {
    const campaign = await campaignService.getById(campaignSummary.id);
    return isCampaignCurrentlyValid(campaign) ? campaign : null;
  } catch {
    return isCampaignCurrentlyValid(campaignSummary) ? campaignSummary : null;
  }
};

export const resolveContact = async (contactId?: number | null): Promise<IContact | null> => {
  if (!contactId || Number(contactId) <= 0) return null;
  try {
    return await contactService.getById(Number(contactId));
  } catch {
    return null;
  }
};

export const resolveEligibleCampaign = async (
  campaignId?: number | null,
  couponCode?: string | null,
  contactId?: number | null
): Promise<ICampaign | null> => {
  const campaign = await resolveCampaign(campaignId, couponCode);
  if (!campaign) return null;

  const contact = await resolveContact(contactId);
  return isCampaignEligible(campaign, contact) ? campaign : null;
};

export type CampaignCouponError = "invalid" | "notApplicable" | "minQuantity";

export type CampaignCouponLookup = {
  campaign: ICampaign | null;
  unitPrice: number | null;
  error: CampaignCouponError | null;
  minimumQuantity: number | null;
};

const emptyCouponLookup: CampaignCouponLookup = {
  campaign: null,
  unitPrice: null,
  error: null,
  minimumQuantity: null,
};

const getCampaignMinimumQuantity = (campaign: ICampaign, productId?: number | null): number => {
  const campaignItem = (campaign.items || []).find(
    (item) => productId && Number(item.productId) === Number(productId)
  );
  const minQuantity = Number(campaignItem?.minimumQuantity);
  return minQuantity > 0 ? minQuantity : 1;
};

export const resolveCampaignCouponForSku = async (params: {
  sku: ISku;
  quantity: number;
  campaignId?: number | null;
  couponCode?: string | null;
  contactId?: number | null;
}): Promise<CampaignCouponLookup> => {
  const code = params.couponCode?.trim() || "";
  const hasCampaignId = Boolean(params.campaignId && Number(params.campaignId) > 0);
  if (!hasCampaignId && !code) return emptyCouponLookup;

  try {
    const campaign = await resolveCampaign(
      hasCampaignId ? Number(params.campaignId) : null,
      code || null
    );
    if (!campaign) {
      return { ...emptyCouponLookup, error: code ? "invalid" : null };
    }

    const contact = await resolveContact(params.contactId);
    if (hasSegmentConstraints(campaign.segment) && !contactMatchesCampaignSegment(campaign, contact)) {
      return { ...emptyCouponLookup, error: "notApplicable" };
    }

    const minimumQuantity = getCampaignMinimumQuantity(campaign, params.sku.productId);
    if (params.quantity < minimumQuantity) {
      return { ...emptyCouponLookup, error: "minQuantity", minimumQuantity };
    }

    const unitPrice = applyCampaignRulesToPrice(
      campaign,
      params.sku.productId,
      getSkuListPrice(params.sku),
      params.quantity
    );
    if (!unitPrice) {
      return { ...emptyCouponLookup, error: "notApplicable", minimumQuantity };
    }

    return { campaign, unitPrice, error: null, minimumQuantity };
  } catch {
    return { ...emptyCouponLookup, error: code ? "invalid" : null };
  }
};

export const applyCampaignPriceToItem = (
  item: IOrderItem,
  campaign: ICampaign | null
): IOrderItem => {
  if (item.lineType === 2) return item;

  const grossUnitPrice = Number(item.grossUnitPrice || item.listPrice || item.netUnitPrice);
  const campaignNetUnitPrice = applyCampaignRulesToPrice(
    campaign,
    item.productId,
    grossUnitPrice,
    item.quantity
  );

  const currentNetUnitPrice = Number(item.netUnitPrice) || 0;
  const netUnitPrice = campaignNetUnitPrice || currentNetUnitPrice || grossUnitPrice;

  return {
    ...item,
    netUnitPrice,
    netLineAmount: item.quantity * netUnitPrice,
    grossLineAmount: item.quantity * grossUnitPrice,
    campaignId: campaign?.id ?? item.campaignId ?? null,
    discountCouponCode: campaign?.code ?? item.discountCouponCode ?? null,
  };
};

export const resolveCampaignNetUnitPrice = async (
  sku: ISku,
  quantity: number,
  campaignId?: number | null,
  couponCode?: string | null,
  contactId?: number | null
): Promise<number | undefined> => {
  const lookup = await resolveCampaignCouponForSku({
    sku,
    quantity,
    campaignId,
    couponCode,
    contactId,
  });
  return lookup.unitPrice || undefined;
};

export const transformSkuToItem = (
  sku: ISku,
  order: IOrderInput | null,
  needComment?: boolean,
  options?: TransformSkuOptions
): IOrderItem => {
  const priceToUse = getSkuListPrice(sku);
  const quantity = options?.quantity && options.quantity > 0 ? options.quantity : 1;
  const netUnitPrice =
    options?.campaignNetUnitPrice && options.campaignNetUnitPrice > 0
      ? options.campaignNetUnitPrice
      : priceToUse;

  return {
    orderNumber: order ? order.orderNumber : null,

    orderKey: order && order.orderKey ? String(order.orderKey) : String(generateRandomNumber()),

    skuID: sku.id ? sku.id : 0,
    productId: sku.productId ? sku.productId : 0,
    skuCode: sku.code ? sku.code : "",
    plaftormSkuCode: sku.code ? sku.code : "",
    quantity: quantity,
    reservedQuantity: sku.reservedStock ? sku.reservedStock : 0,
    lineType: 1,
    listPrice: priceToUse,
    grossUnitPrice: priceToUse,
    netUnitPrice: netUnitPrice,

    netLineAmount: calculateNetLineAmount(quantity, Number(netUnitPrice)),
    grossLineAmount: calculateGrossLineAmount(quantity, Number(priceToUse)),

    grossWeigth: sku.weight ? sku.weight : 0,
    netWeigth: sku.weight ? sku.weight : 0,
    description: sku.title ? sku.title : "",

    variations: sku.variations ? (sku.variations as string) : "",
    userComment: null,
    needComment: needComment || false,
    discountCouponCode: options?.discountCouponCode || null,
    campaignId: options?.campaignId || null,
  };
};
